import React, { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiTrendingUp, FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight } = FiIcons

const AuthPage = () => {
  const { user, signIn, signUp } = useAuth()
  const [isLogin, setIsLogin] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (isLogin) {
        const { error } = await signIn(email, password)
        if (error) throw error
      } else {
        const { error } = await signUp(email, password)
        if (error) throw error
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-lux-light-bg dark:bg-lux-dark-bg text-gray-900 dark:text-white flex items-center justify-center p-4 relative overflow-hidden transition-colors duration-500">
      {/* Background Ambience */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-lux-primary/10 dark:bg-lux-primary/20 rounded-full blur-[120px]"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-lux-accent/10 dark:bg-lux-accent/20 rounded-full blur-[120px]"></div>

      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Floating Decoration */}
        <motion.div 
          animate={{ y: [0, -10, 0] }}
          transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
          className="absolute -top-20 -left-10 hidden sm:block"
        >
          <div className="w-16 h-16 rounded-[2rem] bg-gradient-to-br from-lux-primary to-lux-accent shadow-glow flex items-center justify-center border border-white/20">
             <SafeIcon icon={FiTrendingUp} className="h-8 w-8 text-white" />
          </div>
        </motion.div>

        <div className="bg-white/80 dark:bg-[#0f172a]/80 backdrop-blur-2xl border border-lux-light-border dark:border-white/10 rounded-[3rem] p-8 sm:p-10 shadow-2xl transition-all duration-500">
          <div className="text-center mb-10">
            <div className="inline-block px-4 py-1.5 mb-6 rounded-full bg-lux-primary/10 text-lux-primary text-[10px] font-black uppercase tracking-widest">
              Trading AI Powered
            </div>
            <h1 className="text-4xl font-black mb-3 tracking-tighter">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 font-medium">
              {isLogin ? 'Access your intelligent trading journal' : 'Start your journey with predictive analytics'}
            </p>
          </div>

          {error && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-8 p-4 bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20 rounded-2xl text-red-600 dark:text-red-400 text-sm text-center font-bold"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest ml-1">Email Address</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                  <SafeIcon icon={FiMail} className="h-5 w-5 text-gray-400 group-focus-within:text-lux-primary transition-colors" />
                </div>
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-14 pr-6 py-4 rounded-2xl bg-gray-50 dark:bg-slate-800/50 border-2 border-transparent focus:border-lux-primary/30 focus:bg-white dark:focus:bg-slate-800 outline-none transition-all text-gray-900 dark:text-white placeholder:text-gray-400"
                  placeholder="name@example.com"
                  required 
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest ml-1">Secure Password</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                  <SafeIcon icon={FiLock} className="h-5 w-5 text-gray-400 group-focus-within:text-lux-primary transition-colors" />
                </div>
                <input 
                  type={showPassword ? 'text' : 'password'} 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-14 pr-14 py-4 rounded-2xl bg-gray-50 dark:bg-slate-800/50 border-2 border-transparent focus:border-lux-primary/30 focus:bg-white dark:focus:bg-slate-800 outline-none transition-all text-gray-900 dark:text-white placeholder:text-gray-400"
                  placeholder="••••••••"
                  required 
                />
                <button 
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-5 flex items-center text-gray-400 hover:text-lux-primary transition-colors"
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="h-5 w-5" />
                </button>
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-lux-primary hover:bg-blue-600 active:scale-[0.98] text-white py-5 rounded-2xl font-black text-lg shadow-xl shadow-lux-primary/20 transition-all flex items-center justify-center space-x-3 group disabled:opacity-50"
            >
              <span>{loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Get Started')}</span>
              {!loading && <SafeIcon icon={FiArrowRight} className="h-5 w-5 group-hover:translate-x-1 transition-transform" />}
            </button>
          </form>

          <div className="mt-10 pt-8 border-t border-gray-100 dark:border-white/5 text-center">
            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
              {isLogin ? "New to the platform?" : "Already a member?"}
              <button 
                onClick={() => setIsLogin(!isLogin)}
                className="ml-2 text-lux-primary hover:text-blue-600 font-black transition-all"
              >
                {isLogin ? 'Create Account' : 'Sign In Now'}
              </button>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default AuthPage